import type { ComponentType } from 'react'
import type { UserRole } from '../../types/auth'
import { StatsWidget } from './StatsWidget'
import { TodaysAppointmentsWidget } from './TodaysAppointmentsWidget'
import { OutstandingInvoicesWidget } from './OutstandingInvoicesWidget'
import { LowStockWidget } from './LowStockWidget'
import { OpenConsultationsWidget } from './OpenConsultationsWidget'
import { RecentConsultationsWidget } from './RecentConsultationsWidget'
import { QuickActionsWidget } from './QuickActionsWidget'
import { PlatformOverviewWidget } from './PlatformOverviewWidget'
import { UsersByRoleWidget } from './UsersByRoleWidget'
import { PendingStaffApplicationsWidget } from './PendingStaffApplicationsWidget'
import { QuickLinksWidget } from './QuickLinksWidget'
import { OrgPracticesWidget } from './OrgPracticesWidget'
import { OrgTodaysAppointmentsWidget } from './OrgTodaysAppointmentsWidget'
import { OrgOutstandingInvoicesWidget } from './OrgOutstandingInvoicesWidget'
import { OrgQuickLinksWidget } from './OrgQuickLinksWidget'

export interface DashboardWidgetDefinition {
  id: string
  title: string
  description: string
  /** Roles allowed to see (and, later, add/remove) this widget. This is the one place
   * that answers "who can see what" on the dashboard — extend here, not per-page,
   * as more roles get their own dashboard. */
  roles: UserRole[]
  /** 'half' widgets pair up two-per-row with an adjacent 'half' widget; 'full' widgets
   * always take the whole row. Purely a layout hint for DashboardWidgetGrid. */
  span: 'full' | 'half'
  component: ComponentType
}

export const DASHBOARD_WIDGETS: DashboardWidgetDefinition[] = [
  // ── Staff (vet practice) ──────────────────────────────────────────────────
  {
    id: 'stats', title: 'Practice Stats',
    description: 'Client, patient, and open-record counts.',
    roles: ['staff'], span: 'full', component: StatsWidget,
  },
  {
    id: 'todays-appointments', title: "Today's Appointments",
    description: "The day's schedule at a glance.",
    roles: ['staff'], span: 'full', component: TodaysAppointmentsWidget,
  },
  {
    id: 'outstanding-invoices', title: 'Outstanding Invoices',
    description: 'Overdue and sent invoice totals, with the most overdue clients.',
    roles: ['staff'], span: 'half', component: OutstandingInvoicesWidget,
  },
  {
    id: 'low-stock', title: 'Low Stock',
    description: 'Products at or below a low-stock threshold.',
    roles: ['staff'], span: 'half', component: LowStockWidget,
  },
  {
    id: 'open-consultations', title: 'Open Consultations',
    description: 'Consultations still in progress.',
    roles: ['staff'], span: 'full', component: OpenConsultationsWidget,
  },
  {
    id: 'recent-consultations', title: 'Recent Consultations',
    description: 'Recently completed consultations.',
    roles: ['staff'], span: 'full', component: RecentConsultationsWidget,
  },
  {
    id: 'quick-actions', title: 'Quick Actions',
    description: 'Shortcuts to common staff tasks.',
    roles: ['staff'], span: 'full', component: QuickActionsWidget,
  },

  // ── Super admin (platform) ────────────────────────────────────────────────
  {
    id: 'platform-overview', title: 'Platform Overview',
    description: 'Total users, organisations, practices, clients, and patients.',
    roles: ['super_admin'], span: 'full', component: PlatformOverviewWidget,
  },
  {
    id: 'users-by-role', title: 'Users by Role',
    description: 'Breakdown of accounts by role, including pending approvals.',
    roles: ['super_admin'], span: 'full', component: UsersByRoleWidget,
  },
  {
    id: 'pending-staff-applications', title: 'Pending Staff Applications',
    description: 'Staff applications awaiting approval or rejection.',
    roles: ['super_admin'], span: 'full', component: PendingStaffApplicationsWidget,
  },
  {
    id: 'admin-quick-links', title: 'Quick Links',
    description: 'Shortcuts to common admin tasks.',
    roles: ['super_admin'], span: 'full', component: QuickLinksWidget,
  },

  // ── Org admin (multi-practice) ────────────────────────────────────────────
  {
    id: 'org-practices', title: 'Your Practices',
    description: "Every practice in the org admin's organisation(s), with a link to manage staff.",
    roles: ['org_admin'], span: 'full', component: OrgPracticesWidget,
  },
  {
    id: 'org-todays-appointments', title: "Today's Appointments (All Practices)",
    description: "Today's schedule aggregated across every practice in the organisation.",
    roles: ['org_admin'], span: 'full', component: OrgTodaysAppointmentsWidget,
  },
  {
    id: 'org-outstanding-invoices', title: 'Outstanding Invoices (All Practices)',
    description: 'Overdue and sent invoice totals aggregated across the organisation.',
    roles: ['org_admin'], span: 'full', component: OrgOutstandingInvoicesWidget,
  },
  {
    id: 'org-quick-links', title: 'Quick Links',
    description: 'Shortcuts to common org-admin tasks.',
    roles: ['org_admin'], span: 'full', component: OrgQuickLinksWidget,
  },
]

export function getWidgetsForRole(role: UserRole): DashboardWidgetDefinition[] {
  return DASHBOARD_WIDGETS.filter(w => w.roles.includes(role))
}
